import React, { useState } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip,
  IconButton,
  Tooltip,
  Link as MuiLink,
  Divider,
  Stack,
} from "@mui/material";
import { useQuery } from "react-query";
import {
  Folder as FolderIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import client from "../../../../API";

const AdminCompanyFiles = ({ companyId }) => {
  const [teamFilter, setTeamFilter] = useState("");

  // Company-level files
  const {
    data: companyFilesResp,
    isLoading: companyFilesLoading,
    refetch: refetchCompanyFiles,
  } = useQuery(
    ["companyFiles", companyId],
    () => client.companies.getFiles(companyId),
    { enabled: !!companyId }
  );

  // Teams for dropdown
  const { data: teamsData } = useQuery(
    ["teamsForFiles", companyId],
    () => client.companies.teams.list(companyId),
    { enabled: !!companyId }
  );

  // Team-level files
  const {
    data: teamFilesResp,
    isLoading: teamFilesLoading,
    refetch: refetchTeamFiles,
  } = useQuery(
    ["teamFiles", teamFilter],
    () => client.companies.teams.getFiles(teamFilter),
    { enabled: !!teamFilter }
  );

  const files = teamFilter
    ? teamFilesResp?.files || []
    : companyFilesResp?.files || [];

  const loading = teamFilter ? teamFilesLoading : companyFilesLoading;

  const handleRefresh = () => {
    if (teamFilter) refetchTeamFiles();
    else refetchCompanyFiles();
  };

  return (
    <Box sx={{ width: "100%" }}>
      {/* Header Controls */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {teamFilter ? "Team Files" : "Company Files"}
        </Typography>

        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>Filter by Team</InputLabel>
            <Select
              label="Filter by Team"
              value={teamFilter}
              onChange={(e) => setTeamFilter(e.target.value)}
              displayEmpty
            >
              <MenuItem value="">
                <em>All Teams / All Users</em>
              </MenuItem>
              {teamsData?.teams?.map((team) => (
                <MenuItem key={team.id} value={team.id}>
                  {team.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Tooltip title="Refresh">
            <IconButton onClick={handleRefresh}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      <Divider sx={{ mb: 2 }} />

      {/* Loading */}
      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Empty State */}
      {!loading && files.length === 0 && (
        <Card
          variant="outlined"
          sx={{
            textAlign: "center",
            p: 4,
            borderStyle: "dashed",
            bgcolor: "background.default",
          }}
        >
          <FolderIcon sx={{ fontSize: 42, color: "text.secondary", mb: 1 }} />
          <Typography color="text.secondary">
            No files found {teamFilter && "for this team"}.
          </Typography>
        </Card>
      )}

      {/* Files Grid */}
      {!loading && files.length > 0 && (
        <Grid container spacing={2}>
          {files.map((file) => {
            const userName = file.user
              ? `${file.user.first_name} ${file.user.last_name}`
              : "—";
            return (
              <Grid item xs={12} sm={6} md={4} lg={3} key={file.id}>
                <Card
                  variant="outlined"
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    position: "relative",
                    transition: "box-shadow .2s, transform .2s",
                    "&:hover": {
                      boxShadow: 4,
                      transform: "translateY(-2px)",
                    },
                  }}
                >
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 1,
                        minWidth: 0,
                      }}
                    >
                      <FolderIcon color="primary" fontSize="small" />
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 600 }}
                        noWrap
                        title={file.filename}
                      >
                        {file.filename}
                      </Typography>
                    </Box>

                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <MuiLink
                        href={file.file_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        underline="hover"
                      >
                        Open File
                      </MuiLink>
                    </Typography>

                    <Stack
                      spacing={0.5}
                      sx={{ typography: "caption", color: "text.secondary" }}
                    >
                      <Box>
                        <strong>Type:</strong> {file.file_type || "N/A"}
                      </Box>
                      <Box>
                        <strong>Uploaded:</strong>{" "}
                        {file.uploaded_at
                          ? new Date(file.uploaded_at).toLocaleString()
                          : "—"}
                      </Box>
                      <Box>
                        <strong>User:</strong> {userName}
                      </Box>
                    </Stack>

                    {file.creator_username && (
                      <Chip
                        size="small"
                        label={`Creator: ${file.creator_username}`}
                        sx={{ mt: 1 }}
                      />
                    )}
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {teamFilter && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="caption" color="text.secondary">
            Showing files for team ID {teamFilter}. Switch back to “All Teams” to
            view the full company file set.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default AdminCompanyFiles;
